"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, Flame, Gauge, Layers, ShieldCheck, Wrench, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: "atex-iecex",
  badge: "Regulatory Standards",
  title: "ATEX & IECEx Compliance",
  description: "Operating in Zone 1/21 and Zone 2/22 environments requires rigorous certification, strict documentation trails, and validated optical/electronic enclosures.",
  icon: ShieldCheck,
  points: ["Rigorous third-party certification conformity", "Zone-specific protection concepts (Ex d, Ex p, Ex i)", "Continuous compliance audit readiness"]
}, {
  id: "explosion-prevention",
  badge: "Safety Engineering",
  title: "Explosion Prevention & Intrinsic Safety",
  description: "Mitigating ignition hazards from electrical energy, thermal dissipation, or electrostatic charge in the presence of flammable vapors, gases, or combustible dusts.",
  icon: Flame,
  points: ["Low-energy intrinsically safe circuit architecture", "Pressurized and purged housing systems", "Certified ingress protection against dust and vapors"]
}, {
  id: "process-resilience",
  badge: "Environmental Stress",
  title: "Temperature & Pressure Resilience",
  description: "Process analytical hardware must maintain high spectroscopic precision while directly interfacing with high-pressure pipelines, cryogenic fluids, or high-temperature reactors.",
  icon: Gauge,
  points: ["Optical probe seals resistant to extreme pressures", "Thermal isolation to prevent wavelength drift", "Compatibility with aggressive cleaning solvents"]
}, {
  id: "restricted-maintenance",
  badge: "Operational Access",
  title: "Maintenance in Restricted Areas",
  description: "Routine servicing in hazardous locations requires specialized hot-work permits, personal protective equipment, and zero-spark protocols that complicate calibration and repairs.",
  icon: Wrench,
  points: ["Minimization of field disassembly requirements", "Remote diagnostics and non-invasive calibration", "Simplified module replacement under strict protocols"]
}];
const zoneCategories = [{
  zone: "Zone 0 / 20",
  detail: "Continuous hazard: Explosive atmosphere present continuously or for long periods."
}, {
  zone: "Zone 1 / 21",
  detail: "Intermittent hazard: Explosive atmosphere likely to occur in normal operation."
}, {
  zone: "Zone 2 / 22",
  detail: "Abnormal hazard: Explosive atmosphere not likely, or persists for only a short period."
}];
export default function ZoneChallenges() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="classifiedzonechallenges" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container space-y-12 md:space-y-16">
        {/* Header Block */}
        <div className="max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              Hazardous Area Operations
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
            Key Challenges in Classified Zone Environments
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Integrating analytical instrumentation into hazardous manufacturing locations demands certified protection methods, mechanical robustness, and compliant maintenance protocols.
          </p>
        </div>

        {/* Zone Overview Pills */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-xl bg-muted/40 border border-border">
          {zoneCategories.map((item, index) => {
          return <div key={item.zone} data-index={index} className="flex items-start gap-3 p-3 rounded-lg bg-card border border-border/60 shadow-2xs">
                <Layers className="size-5 text-primary shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <span className="text-sm font-semibold text-foreground block">
                    {item.zone}
                  </span>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {item.detail}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {/* Challenges Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {challenges.map((challenge, index) => {
          const Icon = challenge.icon;
          return <motion.div key={challenge.id} data-index={index} variants={itemVariants} className="h-full">
                <Card className="h-full border border-border bg-card shadow-xs transition-all hover:shadow-md hover:border-border/80 flex flex-col justify-between">
                  <CardHeader className="space-y-3 pb-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="p-2.5 rounded-lg bg-secondary text-foreground">
                        <Icon className="size-5 text-primary" />
                      </div>
                      <Badge variant="outline" className="text-xs font-medium text-muted-foreground border-border">
                        {challenge.badge}
                      </Badge>
                    </div>
                    <div className="space-y-1.5">
                      <CardTitle className="text-xl font-semibold text-foreground">
                        {challenge.title}
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                        {challenge.description}
                      </CardDescription>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0 pb-6">
                    <div className="space-y-2.5 border-t border-border/60 pt-4">
                      {challenge.points.map((point, pointIndex) => <div key={pointIndex} data-index={pointIndex} className="flex items-center gap-2 text-xs md:text-sm text-foreground/90">
                          <CheckCircle2 className="size-4 text-primary shrink-0" />
                          <span>{point}</span>
                        </div>)}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Technical Callout & Next Action */}
        <div className="rounded-xl border border-border bg-muted/30 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4 max-w-2xl">
            <div className="p-3 rounded-lg bg-primary/10 text-primary shrink-0 mt-1">
              <AlertTriangle className="size-6 text-primary" />
            </div>
            <div className="space-y-1">
              <h3 className="text-base md:text-lg font-semibold text-foreground">
                Evaluating Classified Zone Compatibility
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Our application specialists verify area classifications, temperature ratings (T-classes), and gas group compatibilities for process analytical technology deployments.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild variant="default" size="default" className="w-full md:w-auto">
              <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                <span>Discuss Your Application</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="default" className="w-full md:w-auto">
              <Link to="/ApplicationConsultationPage">
                Consultation Details
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}